import React, {useState} from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    TablePagination,
    Box,
    Collapse,
    Typography,
    Checkbox,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import * as util from "node:util";
import digitsOnly from "../utils/digitsOnly";
import {useTranslation} from "react-i18next";

// Улучшенный интерфейс с явным указанием id
interface AccordionTableProps<T extends { id: string | number }, C> {
    data: T[]; // Данные родительской таблицы
    childrenField: keyof T; // Название поля, содержащего дочерние данные
    parentHeaders: { id: keyof T; label: string, col: { type: "dictionary" | "common" | "itn", dictName?: string} }[]; // Заголовки родительской таблицы
    childHeaders: { id: keyof C; label: string }[]; // Заголовки дочерней таблицы
    totalCount: number; // Общее количество записей для пагинации
    page: number;
    pageSize: number;
    onPageChange: (newPage: number) => void; // Функция изменения страницы
    onPageSizeChange: (newPageSize: number) => void; // Функция изменения размера страницы
    onRowClick?: (row: T) => void; // Опциональный обработчик клика на строку
    onSelectionChange?: (rows: T[]) => void; // Новый обработчик для множественного выбора
    childTitle?: (parent: T) => string; // Функция для генерации заголовка детей
    noChildrenMessage?: string; // Сообщение при отсутствии дочерних элементов
    expandAll?: boolean;
}

const formatValue = (value: any): React.ReactNode => {
    // Проверка на объект Date
    if (value && typeof value === 'object' && 'getTime' in value && typeof value.getTime === 'function') {
        return new Date(value).toLocaleDateString();
    }

    // Проверка на строку в формате даты
    if (typeof value === 'string') {
        const datePattern = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d+)?(Z|[+-]\d{2}:\d{2})?)?$/;
        if (datePattern.test(value)) {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString();
            }
        }
    }

    return value;
};

// Улучшенная версия ExpandableRow с явной типизацией для id
const ExpandableRow = <T extends { id: string | number }, C extends object>({
                                                                                row,
                                                                                parentHeaders,
                                                                                childHeaders,
                                                                                childrenField,
                                                                                childTitle,
                                                                                noChildrenMessage = 'Нет данных',
                                                                                expandAll = true,
                                                                                isSelected,
                                                                                onRowSelect,
                                                                            }: {
    row: T;
    parentHeaders: { id: keyof T; label: string, col: { type: "dictionary" | "common" | "itn", dictName?: string} }[];
    childHeaders: { id: keyof C; label: string }[];
    childrenField: keyof T;
    childTitle?: (parent: T) => string;
    noChildrenMessage?: string;
    expandAll?: boolean;
    isSelected: boolean;
    onRowSelect: (event: React.MouseEvent<HTMLTableRowElement> | React.ChangeEvent<HTMLInputElement>, row: T) => void;
}) => {
    const [open, setOpen] = useState(expandAll);
    const childData = row[childrenField] as unknown as C[];
    const hasChildren = Array.isArray(childData) && childData.length > 0;
    const {t} = useTranslation();
    const toggleOpen = () => {
        setOpen(!open);
    };

    const renderChildValue = (item: C, key: keyof C) => {
        const value = item[key];
        return formatValue(value);
    };

    const renderParentCell = (col: {type: "dictionary" | "common" | "itn", dictName?: string}, value: any) =>{
        if(col.type === "common") {
            return <React.Fragment>{value}</React.Fragment>;
        }
        else if(col.type === "dictionary") {
            return <React.Fragment>{t(`${col!.dictName}.${value}`)}</React.Fragment>;
        }
        return digitsOnly(value);
    }

    return (
        <>
            <TableRow
                sx={{'& > *': {borderBottom: 'unset'}}}
                hover
                onClick={(event) => onRowSelect(event, row)}
                selected={isSelected}
            >
                <TableCell padding="checkbox">
                    <Checkbox
                        checked={isSelected}
                        onChange={(event) => onRowSelect(event, row)}
                        color="primary"
                    />
                </TableCell>

                <TableCell>
                    <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            toggleOpen();
                        }}
                    >
                        {open ? <KeyboardArrowUpIcon/> : <KeyboardArrowDownIcon/>}
                    </IconButton>
                </TableCell>

                {parentHeaders.map((header) => (
                    <TableCell key={String(header.id)} sx={{ fontWeight: 500 }}>
                         {renderParentCell(header.col, formatValue(row[header.id]))}
                    </TableCell>
                ))}
            </TableRow>

            <TableRow>
                {/* Исправлен colSpan с учетом чекбокса и иконки разворачивания */}
                <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={parentHeaders.length + 2}>
                    <Collapse in={open} timeout="auto" unmountOnExit>
                        <Box sx={{margin: 1}}>
                            <Typography variant="subtitle2" gutterBottom component="div">
                                {childTitle ? childTitle(row) : `Дочерние элементы`}
                            </Typography>
                            {hasChildren ? (
                                <Table size="small" aria-label="child-table">
                                    <TableHead>
                                        <TableRow>
                                            {childHeaders.map((header) => (
                                                <TableCell key={String(header.id)}>{header.label}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {childData.map((childRow, index) => (
                                            <TableRow key={index} sx={{
                                                '&:last-child td, &:last-child th': {
                                                    borderBottom: 0
                                                }
                                            }}>
                                                {childHeaders.map((header) => (
                                                    <TableCell key={String(header.id)}>
                                                        {renderChildValue(childRow, header.id)}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            ) : (
                                <Typography align="center">
                                    {noChildrenMessage}
                                </Typography>
                            )}
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

// Улучшенный компонент таблицы-аккордиона
function AccordionTable<T extends { id: string | number }, C extends object>({
                                                                                 data,
                                                                                 childrenField,
                                                                                 parentHeaders,
                                                                                 childHeaders,
                                                                                 totalCount,
                                                                                 page,
                                                                                 pageSize,
                                                                                 onPageChange,
                                                                                 onPageSizeChange,
                                                                                 onRowClick,
                                                                                 onSelectionChange,
                                                                                 childTitle,
                                                                                 noChildrenMessage = 'Нет данных',
                                                                                 expandAll = false
                                                                             }: AccordionTableProps<T, C>) {
    const [selectedRows, setSelectedRows] = useState<T[]>([]);

    // Обработчики для пагинации
    const handleChangePage = (_: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        onPageChange(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        onPageSizeChange(parseInt(event.target.value, 10));
        onPageChange(0); // Сбрасываем на первую страницу при изменении размера
    };

    // Проверка выбора строки
    const isSelected = (row: T) => {
        return selectedRows.some(selected => selected.id === row.id);
    };

    // Обработчик выбора строки
    const handleRowSelect = (event: React.MouseEvent<HTMLTableRowElement> | React.ChangeEvent<HTMLInputElement>, row: T) => {
        event.stopPropagation();

        const isRowSelected = isSelected(row);
        let newSelected: T[] = [];

        if (!isRowSelected) {
            newSelected = [...selectedRows, row];
        } else {
            newSelected = selectedRows.filter(selected => selected.id !== row.id);
        }

        setSelectedRows(newSelected);

        // Вызываем обработчик множественного выбора, если он предоставлен
        if (onSelectionChange) {
            onSelectionChange(newSelected);
        }

        // Обработка одиночного клика, если он предоставлен
        if (onRowClick && event.type === 'click') {
            onRowClick(row);
        }
    };

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = [...data];
            setSelectedRows(newSelected);

            if (onSelectionChange) {
                onSelectionChange(newSelected);
            }
            return;
        }

        setSelectedRows([]);

        if (onSelectionChange) {
            onSelectionChange([]);
        }
    };

    return (
        <Paper sx={{width: '100%', overflow: 'hidden'}}>
            <TableContainer>
                <Table stickyHeader aria-label="collapsible table">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={selectedRows.length > 0 && selectedRows.length < data.length}
                                    checked={data.length > 0 && selectedRows.length === data.length}
                                    onChange={handleSelectAllClick}
                                />
                            </TableCell>
                            {/* Ячейка для иконок разворачивания */}
                            <TableCell />

                            {parentHeaders.map((header) => (
                                <TableCell key={String(header.id)}>{header.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.length > 0 ? (
                            data.map((row, index) => (
                                <ExpandableRow
                                    key={row.id.toString() || index}
                                    row={row}
                                    parentHeaders={parentHeaders}
                                    childHeaders={childHeaders}
                                    childrenField={childrenField}
                                    childTitle={childTitle}
                                    noChildrenMessage={noChildrenMessage}
                                    expandAll={expandAll}
                                    isSelected={isSelected(row)}
                                    onRowSelect={handleRowSelect}
                                />
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={parentHeaders.length + 2} align="center">
                                    Нет данных
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Строк на странице:"
                labelDisplayedRows={({from, to, count}) =>
                    `${from}-${to} из ${count !== -1 ? count : `более чем ${to}`}`
                }
            />
        </Paper>
    );
}

export default AccordionTable;